import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { NewsContentType } from '@/types/newsService';

/**
 * News tab UI state — sort, category filter, content-type filter, plus the
 * client-side caches (votes, read articles) that survive page transitions
 * but not full reloads. Mirrors mobile-app/src/store/news.store.ts.
 */
interface NewsSliceState {
  activeSort: 'latest' | 'trending' | 'top';
  activeCategorySlug: string | null;
  activeContentType: NewsContentType | null;
  locale: string;
  userVotes: Record<string, 1 | -1 | null>;
  readSlugs: string[];
  articleSlugs: string[];
}

const initialState: NewsSliceState = {
  activeSort: 'latest',
  activeCategorySlug: null,
  activeContentType: null,
  locale: 'en',
  userVotes: {},
  readSlugs: [],
  articleSlugs: [],
};

const newsSlice = createSlice({
  name: 'news',
  initialState,
  reducers: {
    setSort(state, action: PayloadAction<'latest' | 'trending' | 'top'>) {
      state.activeSort = action.payload;
    },
    setCategory(state, action: PayloadAction<string | null>) {
      state.activeCategorySlug = action.payload;
    },
    setContentType(state, action: PayloadAction<NewsContentType | null>) {
      state.activeContentType = action.payload;
    },
    setNewsLocale(state, action: PayloadAction<string>) {
      state.locale = action.payload;
    },
    setUserVote(state, action: PayloadAction<{ articleId: string; vote: 1 | -1 | null }>) {
      state.userVotes[action.payload.articleId] = action.payload.vote;
    },
    markAsRead(state, action: PayloadAction<string>) {
      if (!state.readSlugs.includes(action.payload)) {
        state.readSlugs.push(action.payload);
      }
    },
    setArticleSlugs(state, action: PayloadAction<string[]>) {
      state.articleSlugs = action.payload;
    },
  },
});

export const {
  setSort,
  setCategory,
  setContentType,
  setNewsLocale,
  setUserVote,
  markAsRead,
  setArticleSlugs,
} = newsSlice.actions;

export default newsSlice.reducer;
